<template>
  <div>
    <div class="custom-file">
      <input
        type="file"
        ref="file"
        :name="uploadFieldName"
        @change="onFilesChange($event.target.name, $event.target.files)"
        @cancel="$emit('cancel')"
        style="display:none"
        accept=".gif, .jpg, .jpeg, .png"
        :multiple="multipleUpload"
        :disabled="disabled"
      >
      <label
        class="custom-file-label text-truncate"
        @click="launchFilePicker()"
        data-test="imageinput-file-name-label"
      >{{ showFileName }}</label>
    </div>
    <div
      class="alert alert-sm alert-danger"
      role="alert"
      v-if="errorDialog"
      data-test="imageinput-error-text"
    >
      {{ errorText }}
    </div>
  </div>
</template>

<script>

import Compressor from 'compressorjs';

export default {
  name: 'BaseImageInput',
  props: {
    value: {
      type: [String, Object],
      default: null,
    },
    compressible: {
      type: Boolean,
      default: true,
    },
    multipleUpload: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      errorDialog: false,
      errorText: '',
      uploadFieldName: 'file',
      uploadImageName: '',
      maxImageSize: 10485760,
      maxImageSizeGIF: 5242880,
      maxImageSizeWithoutCompression: 1048576,
      compressorSettings: {
        convertSize: Infinity,
        height: 600,
        width: 1000,
        loose: true,
        mimeType: 'image/jpeg',
      },
    };
  },
  computed: {
    showFileName() {
      if (this.uploadImageName === '') return 'Choose file';
      return this.uploadImageName;
    },
  },
  methods: {
    launchFilePicker() {
      this.$refs.file.click();
    },
    reset() {
      const input = this.$refs.file;
      input.type = 'text';
      input.type = 'file';
      input.value = '';
      this.uploadImageName = '';
      this.$emit('input', null);
    },
    onFilesChange(fieldName, files) {
      this.errorDialog = false;
      this.errorText = '';

      if (files.length > 0) {
        const filesArray = Array.from(files);
        if (this.areFilesValid(filesArray)) {
          this.$emit('uploadIsStarted');
          filesArray.forEach((file) => this.uploadFile(fieldName, file));
          this.$emit('uploadIsFinished');
        }
      }
    },
    isType(file, extension) {
      return file.name.toLowerCase().endsWith(extension);
    },
    sizeAcceptable(file) {
      if (this.isType(file, '.gif')) {
        return file.size > this.maxImageSizeGIF;
      }
      return file.size > this.maxImageSize;
    },
    bytesToMB(bytes) {
      return bytes / 1024 / 1024;
    },
    areFilesValid(files) {
      if (files.some((file) => !file.type.match('image.*'))) {
        this.errorDialog = true;
        this.errorText = this.multipleUpload
          ? 'Please choose only image files'
          : 'Please choose an image file';
      }
      if (files.some((file) => this.sizeAcceptable(file))) {
        this.errorDialog = true;
        const maxImageSizeInMB = this.bytesToMB(this.maxImageSize);
        const maxImageSizeGIFInMB = this.bytesToMB(this.maxImageSizeGIF);
        this.errorText = this.multipleUpload
          ? `Some of your files are too big! Please select all images under ${maxImageSizeInMB}MB (${maxImageSizeGIFInMB}MB for .gif)`
          : `Your file is too big! Please select an image under ${maxImageSizeInMB}MB (${maxImageSizeGIFInMB}MB for .gif)`;
      }
      if (this.errorDialog) {
        this.$emit('uploadIsInvalid', this.errorText);
        this.reset();
      }
      return !this.errorDialog;
    },
    isCompressible(file) {
      return !this.isType(file, '.gif') && this.compressible && file.size > this.maxImageSizeWithoutCompression;
    },
    uploadFile(fieldName, imageFile) {
      this.uploadImageName = imageFile.name;
      if (this.isCompressible(imageFile)) {
        const compressorSettingsLocal = { ...this.compressorSettings };
        compressorSettingsLocal.file = imageFile;
        compressorSettingsLocal.success = async (result) => this.sendImage(result, fieldName);
        return new Compressor(imageFile, compressorSettingsLocal);
      }
      this.sendImage(imageFile, fieldName);
    },
    sendImage(imageFile, fieldName) {
      const formData = new FormData();
      const previewURL = URL.createObjectURL(imageFile);
      formData.append(fieldName, imageFile);
      this.$emit('input', { formData, previewURL });
    },
  },
};
</script>
